import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import axios from "axios";
import { useLocation } from "react-router-dom";
import Seo from "@/components/Seo";
import SmoothScroll from "@/components/landing/SmoothScroll";
import Navbar from "@/components/landing/Navbar";
import Hero from "@/components/landing/Hero";
import About from "@/components/landing/About";
import Program from "@/components/landing/Program";
import Facilities from "@/components/landing/Facilities";
import Teachers from "@/components/landing/Teachers";
import Gallery from "@/components/landing/Gallery";
import Testimonials from "@/components/landing/Testimonials";
import Registration from "@/components/landing/Registration";
import Footer from "@/components/landing/Footer";
import FloatingWhatsApp from "@/components/landing/FloatingWhatsApp";
import FloatingVideo from "@/components/landing/FloatingVideo";
import Iklan from "@/components/landing/Iklan";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatsBand() {
  const [stats, setStats] = useState({
    total_santri_aktif: 100,
    total_hafidz_alumni: 400,
    total_pengajar: 15,
    tahun_berdiri: 2013,
  });

  useEffect(() => {
    axios.get(`${API}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const items = [
    { label: "Santri Aktif", value: `${stats.total_santri_aktif}+` },
    { label: "Hafizh & Alumni", value: `${stats.total_hafidz_alumni}+` },
    { label: "Ustadz & Ustadzah", value: `${stats.total_pengajar}` },
    { label: "Tahun Berdiri", value: `${stats.tahun_berdiri}` },
  ];

  return (
    <section
      className="relative bg-parchment py-20 border-y border-gold/20"
      data-testid="stats-band"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-10">
        {items.map((it, i) => (
          <motion.div
            key={it.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: i * 0.1 }}
            className="text-left"
            data-testid={`stat-${i}`}
          >
            <div className="font-serif-display text-forest text-5xl md:text-6xl tracking-tight">
              {it.value}
            </div>
            <div className="mt-2 text-xs tracking-[0.3em] uppercase text-moss">
              {it.label}
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function Marquee() {
  return (
    <div className="bg-forest text-parchment py-6 overflow-hidden border-y border-gold/30">
      <div className="flex animate-marquee whitespace-nowrap">
        {Array.from({ length: 2 }).map((_, k) => (
          <div key={k} className="flex items-center gap-12 px-6">
            {["تَدَبُّر • Tadabbur", "حِفْظ • Hifzh", "تَجْوِيد • Tajwid", "إِتْقَان • Itqan", "أَدَب • Adab", "تَرْبِيَة • Tarbiyah"].map((t, i) => (
              <span key={`${k}-${i}`} className="font-arabic text-2xl md:text-3xl text-parchment/80 flex items-center gap-12">
                {t}
                <span className="w-2 h-2 rounded-full bg-gold" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useTransform(scrollYProgress, [0, 1], [0, 1]);
  return (
    <motion.div
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-[2px] bg-gold origin-left z-[60]"
      data-testid="scroll-progress"
    />
  );
}

export default function Landing() {
  const [gallery, setGallery] = useState(null);
  const location = useLocation();

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => setGallery(r.data)).catch(() => {});
  }, []);

  // Support hash scrolling when arriving from another route (e.g. /#tentang)
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace(/^#/, "");
    if (!id) return;
    // Delay to allow sections to mount
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const navOffset = 88;
      const targetY = el.getBoundingClientRect().top + window.scrollY - navOffset;
      const lenis = window.__lenis;
      if (lenis && typeof lenis.scrollTo === "function") lenis.scrollTo(targetY, { duration: 1.1 });
      else window.scrollTo({ top: targetY, behavior: "smooth" });
    }, 400);
    return () => clearTimeout(t);
  }, [location.hash]);

  return (
    <main className="bg-parchment text-forest min-h-screen overflow-x-hidden">
      <Seo
        title="Membentuk Generasi Rabbani Penjaga Kalamullah"
        description="Pondok Tahfizh Generasi Rabbani Qurani — rumah bagi para penghafal Al-Qur'an dengan metode tahsin, tahfizh, dan mutqin yang terstruktur, didampingi para ustadz yang amanah."
      />
      <SmoothScroll />
      <ScrollProgress />
      <Navbar />
      <Hero />
      <StatsBand />
      <Iklan gallery={gallery?.iklan} />
      <About gallery={gallery?.tentang} />
      <Marquee />
      <Program />
      <Facilities gallery={gallery?.fasilitas} />
      <Teachers gallery={gallery?.pengajar} />
      <Gallery gallery={gallery?.galeri} />
      <Testimonials />
      <Registration gallery={gallery?.pendaftaran} />
      <Footer />
      <FloatingVideo />
      <FloatingWhatsApp />
    </main>
  );
}
